import {
	ANALYTICS_SOURCE,
	INCOMING_LINKS_ACTION_SUBJECT,
	INCOMING_LINKS_LOAD_MORE_ACTION_SUBJECT,
	INCOMING_LINKS_REMOTE_LINKS_ACTION_SUBJECT,
} from '../analytics.ts';
import type { LinkedJiraWorkItemsErrorType } from '../utils/fetchLinkedJiraWorkItemUrls.ts';

/** Category of an incoming-links fetch failure. */
export type IncomingLinksErrorType = 'network' | 'graphql';

/**
 * Fired once after the initial graph fetch and ORS hydration settle. `count` is the links the
 * byline can render, which is what the initial catch-up loop pages towards — not the raw graph
 * rows, many of which resolve to nothing.
 */
export const getIncomingLinksSucceededEvent = (attributes: {
	count: number;
}): {
	readonly type: 'sendOperationalEvent';
	readonly data: {
		readonly action: 'succeeded';
		readonly actionSubject: 'incomingLinks';
		readonly source: 'byline-connections';
		readonly attributes: { count: number };
	};
} =>
	({
		type: 'sendOperationalEvent',
		data: {
			action: 'succeeded',
			actionSubject: INCOMING_LINKS_ACTION_SUBJECT,
			source: ANALYTICS_SOURCE,
			attributes,
		},
	}) as const;

/**
 * Fired when the incoming-links graph query errors (network or GraphQL).
 *
 * A partial GraphQL error still returns usable rows, so this can fire alongside a succeeded
 * event for the same hook instance.
 */
export const getIncomingLinksFailedEvent = (attributes: {
	errorType: IncomingLinksErrorType;
}): {
	readonly type: 'sendOperationalEvent';
	readonly data: {
		readonly action: 'failed';
		readonly actionSubject: 'incomingLinks';
		readonly source: 'byline-connections';
		readonly attributes: { errorType: IncomingLinksErrorType };
	};
} =>
	({
		type: 'sendOperationalEvent',
		data: {
			action: 'failed',
			actionSubject: INCOMING_LINKS_ACTION_SUBJECT,
			source: ANALYTICS_SOURCE,
			attributes,
		},
	}) as const;

/**
 * Fired after each explicit `loadMore` page hydrates. `count` is that page's newly resolved
 * links, not a running total.
 *
 * This carries its own `actionSubject` rather than an attribute on the succeeded event above,
 * because GAS identifies an event by `actionSubject` + `action`: sharing a subject would merge
 * initial-page and load-more counts into one metric. It is also why the old
 * `fireOperationalAnalytics(event, 'incomingLinks loadMore succeeded', …)` call could never
 * work — that helper parses its string as `subject action` and throws on a third word. CCINT-9105
 */
export const getIncomingLinksLoadMoreSucceededEvent = (attributes: {
	count: number;
}): {
	readonly type: 'sendOperationalEvent';
	readonly data: {
		readonly action: 'succeeded';
		readonly actionSubject: 'incomingLinksLoadMore';
		readonly source: 'byline-connections';
		readonly attributes: { count: number };
	};
} =>
	({
		type: 'sendOperationalEvent',
		data: {
			action: 'succeeded',
			actionSubject: INCOMING_LINKS_LOAD_MORE_ACTION_SUBJECT,
			source: ANALYTICS_SOURCE,
			attributes,
		},
	}) as const;

/**
 * Fired once the Jira remote-link fallback has settled and merged. `fetched` is the work-item urls
 * the REST endpoint returned, `resolved` those ORS hydrated, and `added` those the graph had not
 * already supplied — so `resolved - added` measures how much the graph has ingested. CCINT-9346
 */
export const getIncomingLinksRemoteLinksSucceededEvent = (attributes: {
	fetched: number;
	resolved: number;
	added: number;
}): {
	readonly type: 'sendOperationalEvent';
	readonly data: {
		readonly action: 'succeeded';
		readonly actionSubject: 'incomingLinksRemoteLinks';
		readonly source: 'byline-connections';
		readonly attributes: { fetched: number; resolved: number; added: number };
	};
} =>
	({
		type: 'sendOperationalEvent',
		data: {
			action: 'succeeded',
			actionSubject: INCOMING_LINKS_REMOTE_LINKS_ACTION_SUBJECT,
			source: ANALYTICS_SOURCE,
			attributes,
		},
	}) as const;

/**
 * Fired when the remote-link request fails. The graph source is unaffected, so this never implies
 * an `incomingLinks failed`.
 */
export const getIncomingLinksRemoteLinksFailedEvent = (attributes: {
	errorType: LinkedJiraWorkItemsErrorType;
}): {
	readonly type: 'sendOperationalEvent';
	readonly data: {
		readonly action: 'failed';
		readonly actionSubject: 'incomingLinksRemoteLinks';
		readonly source: 'byline-connections';
		readonly attributes: { errorType: LinkedJiraWorkItemsErrorType };
	};
} =>
	({
		type: 'sendOperationalEvent',
		data: {
			action: 'failed',
			actionSubject: INCOMING_LINKS_REMOTE_LINKS_ACTION_SUBJECT,
			source: ANALYTICS_SOURCE,
			attributes,
		},
	}) as const;
